import { useCallback, useEffect, useRef, useState } from 'react'
import { useMediaObjectUrl } from '@/components/media/MediaLightbox'
import type { ModelGroup, ModelGroupMember } from '@/lib/api/schemas/inventory.schema'
import { ValueOrReason } from '@/components/warehouse/primitives'
import { publicationStateLabels } from '@/lib/warehouse/labels'

function VariationThumbnail({ member }: { member: ModelGroupMember }) {
  const { objectUrl } = useMediaObjectUrl(member.thumbnailUrl)
  if (objectUrl === null) return <span aria-hidden="true" className="grid size-7 place-items-center rounded-full bg-bone text-gold-deep">◆</span>
  return <img src={objectUrl} alt="" className="size-7 rounded-full object-cover" />
}

function VariationChip({ member, onPick, measure }: {
  member: ModelGroupMember
  onPick: (member: ModelGroupMember) => void
  measure?: (node: HTMLButtonElement | null) => void
}) {
  return (
    <button
      ref={measure}
      type="button"
      onClick={() => onPick(member)}
      aria-current={member.isCurrent ? 'true' : undefined}
      className={`flex h-10 shrink-0 items-center gap-2 rounded-lg border px-1.5 text-left shadow-none ${member.isCurrent ? 'border-gold-deep bg-gold/5' : 'border-rule bg-paper'}`}
    >
      <VariationThumbnail member={member} />
      <span className="min-w-0 pr-1">
        <span className="block truncate text-xs font-semibold tabular-nums text-ink">{member.article ?? 'Без артикула'}</span>
        <span className="block max-w-36 truncate text-[10px] text-ink-muted"><ValueOrReason value={member.differentiator} reason={member.differentiatorUnavailableReason} message={member.differentiatorUnavailableMessage} /></span>
      </span>
    </button>
  )
}

/** Server-owned model siblings with width-measured overflow and typed absences. */
export function VariationFeed({ model, onPick }: { model: ModelGroup; onPick: (canonicalProductId: string) => void }) {
  const rowRef = useRef<HTMLDivElement | null>(null)
  const measuredRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [visibleCount, setVisibleCount] = useState(model.group?.members.length ?? 0)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const measure = useCallback(() => {
    const row = rowRef.current
    const group = model.group
    if (row === null || group === null) return
    const available = row.getBoundingClientRect().width
    if (available <= 0) {
      setVisibleCount(group.members.length)
      return
    }
    const widths = measuredRefs.current.map((node) => node?.getBoundingClientRect().width ?? 0)
    const overflowWidth = 48
    let used = 0
    let count = 0
    for (const width of widths) {
      const next = used + (count === 0 ? 0 : 8) + width
      const reserve = count + 1 < widths.length ? overflowWidth + 8 : 0
      if (next + reserve > available) break
      used = next
      count += 1
    }
    setVisibleCount(Math.max(1, count))
  }, [model.group])

  useEffect(() => {
    measure()
    const row = rowRef.current
    if (row === null || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => observer.disconnect()
  }, [measure])

  if (model.group === null) {
    // Model absence is structural for this population; the detail header keeps
    // the reason in its title while the feed stays silent.
    return null
  }
  if (model.group.memberCount === 1) {
    return (
      <p className="text-xs text-ink-muted" data-model-absence="one-member">
        В модели один артикул · выбирать другой товар не требуется.
      </p>
    )
  }

  const group = model.group
  const hidden = Math.max(0, group.memberCount - visibleCount)
  const filtered = group.members.filter((member) => (
    (member.article ?? '').toLocaleLowerCase('ru').includes(query.trim().toLocaleLowerCase('ru'))
  ))
  const pick = (member: ModelGroupMember) => {
    onPick(member.productId)
  }

  return (
    <section className="relative" aria-label={`Модель ${group.modelArticle} · артикулов: ${group.memberCount}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold text-ink-2">Модель {group.modelArticle} · артикулов: {group.memberCount}</p>
        {group.publicationGroups.length > 0 ? <ul className="flex flex-wrap items-center gap-2 text-[10px]">{group.publicationGroups.map((publication) => <li key={publication.accountCode} className="rounded-full bg-bone px-2 py-1 text-ink-2">{publication.displayName} · <ValueOrReason value={publication.state === 'unknown' ? null : publicationStateLabels[publication.state]} reason={publication.unavailableReason} /></li>)}</ul> : null}
      </div>
      <div ref={rowRef} className="mt-2 flex min-w-0 gap-2 overflow-hidden">
        {group.members.slice(0, visibleCount).map((member) => <VariationChip key={member.productId} member={member} onPick={pick} />)}
        {hidden > 0 ? <button type="button" onClick={() => setOpen((value) => !value)} className="h-10 shrink-0 rounded-lg border border-rule bg-paper px-3 text-xs font-semibold">+{hidden}</button> : null}
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute invisible flex gap-2">
        {group.members.map((member, index) => <VariationChip key={member.productId} member={member} onPick={pick} measure={(node) => { measuredRefs.current[index] = node }} />)}
      </div>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-2 w-full rounded-lg border border-rule bg-paper p-3 shadow-lg">
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по артикулу" className="h-9 w-full rounded-md border border-rule px-3 text-xs" />
          <p className="mt-2 text-[10px] text-ink-muted">Артикулы модели уходят на Wildberries одной карточкой. Группу собирает сервер по нормализованному артикулу, бренду, типу изделия и общим медиа.</p>
          {group.publicationGroups.length > 0 ? <ul className="mt-2 text-[10px] text-ink-2">{group.publicationGroups.map((publication) => <li key={publication.accountCode}>{publication.accountCode} · <ValueOrReason value={publication.state === 'unknown' ? null : publicationStateLabels[publication.state]} reason={publication.unavailableReason} /></li>)}</ul> : null}
          <ul className="mt-3 grid max-h-64 grid-cols-3 gap-2 overflow-y-auto">
            {filtered.map((member) => <li key={member.productId}><VariationChip member={member} onPick={pick} /></li>)}
          </ul>
        </div>
      ) : null}
    </section>
  )
}
